// src/components/RemindersModal.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash, X, Bell, Check } from "phosphor-react";
import { api } from "../api";

export default function RemindersModal({
  open = true,
  onClose,
  storageKey = "reminders:global",
  variant = "modal",
  className = "",
  showClose,
}) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const isModal = variant === "modal";
  const shouldShowClose = showClose ?? isModal;

  const activeCount = useMemo(
    () => items.filter((i) => !i.done).length,
    [items]
  );

  const doneCount = useMemo(
    () => items.filter((i) => i.done).length,
    [items]
  );

  const load = async () => {
    if (isModal && !open) return;

    try {
      setLoading(true);
      const { data } = await api.get("/reminders");
      const arr = Array.isArray(data) ? data : [];
      setItems(arr);

      try {
        localStorage.setItem(storageKey, JSON.stringify(arr));
      } catch {}
    } catch {
      try {
        const raw = localStorage.getItem(storageKey);
        setItems(raw ? JSON.parse(raw) : []);
      } catch {
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModal ? open : storageKey]);

  const add = async () => {
    const t = text.trim();
    if (!t) return;

    setText("");

    try {
      const { data } = await api.post("/reminders", { text: t });
      setItems((prev) => [data, ...prev]);
    } catch {
      const local = { id: Date.now(), text: t, done: false, __local: true };
      setItems((prev) => [local, ...prev]);
    }
  };

  const toggle = async (id) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;

    const nextDone = !it.done;

    setItems((prev) =>
      prev.map((x) => (x.id === id ? { ...x, done: nextDone } : x))
    );

    try {
      if (!it.__local) await api.patch(`/reminders/${id}`, { done: nextDone });
    } catch {
      setItems((prev) =>
        prev.map((x) => (x.id === id ? { ...x, done: !nextDone } : x))
      );
    }
  };

  const remove = async (id) => {
    const was = items;

    setItems((prev) => prev.filter((i) => i.id !== id));

    try {
      const it = was.find((x) => x.id === id);
      if (it && !it.__local) await api.delete(`/reminders/${id}`);
    } catch {
      setItems(was);
    }
  };

  const clearDone = async () => {
    const doneItems = items.filter((i) => i.done);
    if (!doneItems.length) return;

    const was = items;

    setItems((prev) => prev.filter((i) => !i.done));

    try {
      await Promise.all(
        doneItems
          .filter((i) => !i.__local)
          .map((i) => api.delete(`/reminders/${i.id}`))
      );
    } catch {
      setItems(was);
    }
  };

  if (isModal && !open) return null;

    const wrapperClass = isModal
      ? "fixed inset-0 z-[150] grid place-items-center bg-black/35 overflow-y-auto p-0 md:p-6"
      : "";

  const cardClass = [
    `
      relative w-full overflow-hidden
      rounded-[28px] sm:border sm:border-blue-100/80 border-none
      sm:shadow-[0_8px_0px_rgba(37,99,235,0.96)]
      sm:bg-white bg-blue-100 text-blue-950 
    `,
    isModal ? "max-w-2xl mx-auto p-4 md:p-6" : "px-3 py-3",
    className,
  ].join(" ");

  return (
    <div className={wrapperClass}>
      <div className={cardClass}>
        {shouldShowClose && (
          <button
            onClick={onClose}
            className="
              absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center text-gray-800
            "
            aria-label="Close"
          >
            <X size={19} weight="bold" />
          </button>
        )}

        <div className="text-center">
          <div className="-mt-4 sm:mt-1 flex items-center justify-center">
            <h2 className="hidden sm:block mx-2 text-blue-600 font-black leading-none tracking-[-0.01em] text-[24px]">
              Reminders
            </h2>
          </div>

          <div className="sm:mt-1 flex items-center justify-center text-sm font-semibold text-blue-600">
            <span>{activeCount} active</span>

            <span className="mx-1.5 sm:mx-2 h-1 w-1 rounded-full bg-gray-700" />

            <span>{doneCount} complete</span>
          </div>
        </div>

        <div className="sm:mt-5 mt-3 rounded-2xl border sm:bg-blue-50 border-none bg-transparent p-3 md:p-4">
          <div className="flex items-center gap-2">
            <div className="!flex-1 !text-sm !rounded-2xl !bg-white !px-3 !min-w-0 !min-h-0 !py-1 !ring-2 !ring-blue-700">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") add();
                }}
                placeholder="Add a reminder"
                className="w-full bg-transparent py-1 text-sm text-gray-800 outline-none placeholder:text-blue-200"
              />
            </div>

            <button
              onClick={add}
              disabled={!text.trim()}
              className={`!inline-flex !items-center !gap-1 !rounded-full !px-3 !min-h-0 !py-1 !text-sm !font-semibold !transition ${
                text.trim()
                  ? "!bg-blue-600 !text-white !hover:bg-blue-900"
                  : "sm:hover:!bg-blue-500 !bg-blue-700 !text-white !cursor-default"
              }`}
            >
              <Plus size={12} weight="bold" />
              Add
            </button>
          </div>

          <ul className="mt-4 max-h-[50vh] space-y-2 py-2 overflow-y-auto sm:pr-1">
            {loading && (
              <li className="rounded-2xl bg-blue-50/60 px-3 py-3 text-sm font-semibold text-blue-500">
                Loading…
              </li>
            )}

            {!loading && items.length === 0 && (
              <li className="rounded-2xl bg-transparent sm:py-0 px-3 py-4">
                <div className="flex items-start gap-2">
                  <div className="grid h-7 w-7 shrink-0 place-items-center text-blue-400">
                    <Bell size={20} weight="fill" />
                  </div>

                  <div>
                    <div className="text-sm font-medium leading-tight text-blue-700">
                      Nothing to remember yet
                    </div>
                    <div className="text-sm text-gray-700">
                      Add quick tasks, follow-ups, or tiny things you do not want
                      to forget
                    </div>
                  </div>
                </div>
              </li>
            )}

            {items.map((it) => (
              <li
                key={it.id}
                className={`
                  group flex items-center justify-between gap-2 rounded-2xl
                  border !px-3 !py-1 !min-h-0 !min-w-0 !h-8 sm:py-2 transition
                  ${
                    it.done
                      ? "border-blue-100 bg-white"
                      : "border-blue-700 bg-blue-700"
                  }
                `}
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggle(it.id)}
                    className={`
                      grid !h-[18px] !w-[18px] shrink-0 place-items-center rounded-full
                      !min-w-0 !min-h-0
                      ${it.done
                        ? "bg-blue-500 text-white"
                        : "border border-blue-100 bg-white"
                      }
                    `}
                  >
                    {it.done && <Check size={12} weight="bold" />}
                  </button>

                  <span
                    className={`truncate text-sm ${
                      it.done
                        ? "text-gray-800 line-through"
                        : "text-white"
                    }`}
                  >
                    {it.text}
                  </span>

                  {it.__local && (
                    <span className="shrink-0 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-400">
                      offline
                    </span>
                  )}
                </div>

                <button
                  onClick={() => remove(it.id)}
                  className="
                    grid h-8 w-8 shrink-0 place-items-center justify-end mr-2 rounded-full
                    text-blue-300 transition hover:bg-white hover:text-blue-600
                  "
                  aria-label="Remove"
                >
                  <Trash size={15} />
                </button>
              </li>
            ))}
          </ul>

          {doneCount > 0 && (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={clearDone}
                className="rounded-full bg-blue-100 !px-3 !py-1.5 !min-h-0 !h-8 text-sm font-medium text-gray-700 transition hover:shadow-sm"
              >
                Clear Completed
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}